import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '@/services/apiClient';

const ThemeContext = createContext(null);

const STORAGE_THEME = 'movez-theme';
const STORAGE_MODE = 'movez-mode';

/** Aplica um mapa de tokens { bg, surf, volt, ... } às CSS custom properties. */
function applyColors(colors) {
  if (!colors) return;
  const root = document.documentElement;
  for (const [key, value] of Object.entries(colors)) {
    root.style.setProperty(`--${key}`, value);
  }
}

export function ThemeProvider({ children }) {
  const [themes, setThemes] = useState([]);
  const [themeCode, setThemeCode] = useState(localStorage.getItem(STORAGE_THEME) || 'flux');
  const [mode, setMode] = useState(localStorage.getItem(STORAGE_MODE) || 'dark');
  const [ready, setReady] = useState(false);

  // Busca os temas ativos da API uma vez.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/themes');
        const list = (res.data || res).themes || [];
        if (!alive) return;
        setThemes(list);
        // Se o tema salvo não existe, cai no padrão.
        const exists = list.some((t) => t.code === themeCode);
        if (!exists) {
          const def = list.find((t) => t.isDefault) || list[0];
          if (def) setThemeCode(def.code);
        }
      } catch {
        // Sem API: mantém os defaults do variables.css (FLUX dark).
      } finally {
        if (alive) setReady(true);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aplica cores sempre que tema/modo/lista mudam.
  useEffect(() => {
    const theme = themes.find((t) => t.code === themeCode);
    if (!theme) return;
    const colors = theme.variants?.[mode] || theme.variants?.dark || theme.variants?.light;
    applyColors(colors);
    localStorage.setItem(STORAGE_THEME, themeCode);
    localStorage.setItem(STORAGE_MODE, mode);
    document.documentElement.setAttribute('data-theme', themeCode);
    document.documentElement.setAttribute('data-mode', mode);
  }, [themes, themeCode, mode]);

  const selectTheme = useCallback((code) => setThemeCode(code), []);
  const toggleMode = useCallback(() => setMode((m) => (m === 'dark' ? 'light' : 'dark')), []);

  const value = {
    themes,
    themeCode,
    mode,
    ready,
    currentTheme: themes.find((t) => t.code === themeCode) || null,
    selectTheme,
    setMode,
    toggleMode,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme deve ser usado dentro de ThemeProvider');
  return ctx;
}
